import { dataFlowLog } from './dataFlowLogger';
import { readStorageItem, writeStorageItem } from './storage';

const RELOAD_THROTTLE_KEY = 'ls:last-main-reload-at';
const RELOAD_THROTTLE_MS = 3500;
const MAIN_RELOAD_WINDOW_MS = 3500;

const resolveNow = () => (typeof Date !== 'undefined' ? Date.now() : 0);

const readLastReload = () => {
  try {
    const last = Number(readStorageItem(RELOAD_THROTTLE_KEY, { area: 'session', fallback: 0 }) || 0);
    return Number.isFinite(last) ? last : 0;
  } catch {
    return 0;
  }
};

export const requestWindowReload = (reason, details = {}) => {
  if (typeof window === 'undefined') return false;

  const {
    delayMs = 0,
    cleanup,
    source = 'app',
    ...rest
  } = details;

  const now = resolveNow();
  const lastReload = readLastReload();
  const inMemoryAt = window.__lsMainReloadInFlightAt || 0;

  if (window.__lsMainReloadInFlightAt && now - inMemoryAt < MAIN_RELOAD_WINDOW_MS) {
    dataFlowLog('app.reload.blocked', {
      reason,
      source,
      blockedBy: 'in-memory',
      ageMs: now - inMemoryAt,
      ...rest,
    });
    return false;
  }

  if (lastReload && now - lastReload < RELOAD_THROTTLE_MS) {
    dataFlowLog('app.reload.blocked', {
      reason,
      source,
      blockedBy: 'session',
      ageMs: now - lastReload,
      ...rest,
    });
    return false;
  }

  window.__lsMainReloadInFlightAt = now;
  writeStorageItem(RELOAD_THROTTLE_KEY, String(now), { area: 'session' });

  dataFlowLog('app.reload.scheduled', {
    reason,
    source,
    delayMs,
    ...rest,
  });

  window.setTimeout(() => {
    const execute = async () => {
      try {
        if (typeof cleanup === 'function') {
          await cleanup();
        }
        dataFlowLog('app.reload.execute', {
          reason,
          source,
        });
        window.location.reload();
      } catch {
        // Ignore cleanup errors and still refresh once to keep previous behaviour.
        dataFlowLog('app.reload.execute', {
          reason,
          source,
          fallback: true,
        });
        window.location.reload();
      }
    };

    void execute();
  }, delayMs);

  return true;
};
